import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDataLoader } from '../hooks/useDataLoader';
import AdminSidebar from './AdminSidebar';
import AdminDashboardHome from './AdminDashboardHome';
import AdminSettings from './AdminSettings';
import ClientsPage from '../pages/ClientsPage';
import InvoicesPage from '../pages/InvoicesPage';
import QuotesPage from '../pages/QuotesPage';
import ReportsPage from '../pages/ReportsPage';
import ClientModal from './modals/ClientModal';
import InvoiceModal from './modals/InvoiceModal';
import QuoteModal from './modals/QuoteModal';
import PaymentModal from './modals/PaymentModal';
import PaymentHistoryModal from './modals/PaymentHistoryModal';
import AdminPaymentProcessingModal from './modals/AdminPaymentProcessingModal';
import { Menu } from 'lucide-react';

const AdminApp = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [currentClientDocuments, setCurrentClientDocuments] = useState([]);
  
  // Sidebar state  
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Handle mobile detection and close mobile sidebar when window is resized to desktop
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobileScreen = window.innerWidth <= 1024;
      setIsMobile(isMobileScreen);
      if (window.innerWidth >= 1024) {
        setMobileSidebarOpen(false);
      }
    };

    checkScreenSize(); // Check on mount
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const {
    clients,
    invoices,
    quotes,
    settings,
    userPermissions,
    loading,
    error,
    saveClient,
    deleteClient,
    saveInvoice,
    deleteInvoice,
    saveQuote,
    deleteQuote,
    recordPayment,
    deletePayment,
    updatePayment,
    saveSettings,
    convertQuoteToInvoice,
    // Document operations
    loadClientDocuments,
    uploadDocument,
    deleteDocument,
    updateDocument,
    downloadDocument
  } = useDataLoader();

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to log out?')) {
      await logout();
    }
  };

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  const handleNavigate = (section) => {
    navigate(`/admin/${section === 'dashboard' ? '' : section}`);
  };

  const openModal = async (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    
    // If opening client modal and editing existing client, load their documents
    if (type === 'client' && item?._id) {
      try {
        const docs = await loadClientDocuments(item._id);
        setCurrentClientDocuments(docs || []);
      } catch (error) {
        console.error('Failed to load client documents:', error);
        setCurrentClientDocuments([]);
      }
    } else {
      setCurrentClientDocuments([]);
    }
    
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setEditingPayment(null);
    setCurrentClientDocuments([]);
  };

  const handleSaveClient = async (clientData) => {
    try {
      await saveClient(clientData, !!editingItem);
      closeModal();
    } catch (error) {
      alert('Error saving client: ' + error.message);
    }
  };

  const handleUploadDocument = async (clientId, file, documentInfo) => {
    try {
      const newDoc = await uploadDocument(clientId, file, documentInfo);
      setCurrentClientDocuments(prev => [newDoc, ...prev]);
      return newDoc;
    } catch (error) {
      console.error('Failed to upload document:', error);
      throw error;
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await deleteDocument(documentId);
      setCurrentClientDocuments(prev => prev.filter(doc => doc._id !== documentId));
    } catch (error) {
      console.error('Failed to delete document:', error);
      throw error;
    }
  };

  const handleUpdateDocument = async (documentId, updates) => {
    try {
      const updatedDoc = await updateDocument(documentId, updates);
      setCurrentClientDocuments(prev => 
        prev.map(doc => doc._id === documentId ? updatedDoc : doc)
      );
      return updatedDoc;
    } catch (error) {
      console.error('Failed to update document:', error);
      throw error;
    }
  };

  const handleSaveInvoice = async (invoiceData) => {
    try {
      await saveInvoice(invoiceData, !!editingItem);
      closeModal();
    } catch (error) {
      alert('Error saving invoice: ' + error.message);
    }
  };

  const handleSaveQuote = async (quoteData) => {
    try {
      await saveQuote(quoteData, !!editingItem);
      closeModal();
    } catch (error) {
      alert('Error saving quote: ' + error.message);
    }
  };






  const handleDeleteItem = async (id, type) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        if (type === 'client') await deleteClient(id);
        if (type === 'invoice') await deleteInvoice(id);
        if (type === 'quote') await deleteQuote(id);
      } catch (error) {
        alert('Error deleting item: ' + error.message);
      }
    }
  };

  const handleRecordPayment = async (paymentData, isEditing = false) => {
    try {
      if (isEditing) {
        await updatePayment(paymentData._id, {
          amount: parseFloat(paymentData.amount),
          paymentDate: paymentData.paymentDate,
          paymentMethod: paymentData.paymentMethod,
          reference: paymentData.reference,
          notes: paymentData.notes
        });
      } else {
        await recordPayment({
          // camelCase: the v2 API reads invoiceId (invoice_id was the PHP API's name).
          invoiceId: editingItem._id,
          amount: parseFloat(paymentData.amount),
          paymentDate: paymentData.paymentDate,
          paymentMethod: paymentData.paymentMethod,
          reference: paymentData.reference,
          notes: paymentData.notes
        });
      }
      closeModal();
    } catch (error) {
      alert(`Error ${isEditing ? 'updating' : 'recording'} payment: ` + error.message);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await deletePayment(paymentId);
      } catch (error) {
        alert('Error deleting payment: ' + error.message);
      }
    }
  };

  const handleProcessPayment = (invoice) => {
    const client = clients.find(c => c._id === invoice.clientId);
    if (client) {
      setEditingItem({ ...invoice, client });
      setModalType('processPayment');
      setShowModal(true);
    }
  };

  const handlePaymentProcessed = async (paymentResult) => {
    try {
      // Payment was successful, refresh data
      // The useDataLoader should automatically refresh
      window.location.reload(); // Simple refresh for now
      
      // Show success message
      const message = `Payment processed successfully!\n` +
        `Wallet: £${paymentResult.walletAmountUsed}\n` +
        `Payment Method: £${paymentResult.paymentMethodAmount}\n` +
        `Total: £${paymentResult.totalAmount}`;
      
      alert(message);
    } catch (error) {
      console.error('Error after payment:', error);
      alert('Payment processed but failed to refresh data. Please reload the page.');
    }
  };

  const handleConvertQuoteToInvoice = async (quote) => {
    try {
      await convertQuoteToInvoice(quote);
    } catch (error) {
      alert('Error converting quote: ' + error.message);
    }
  };

  const sendEmail = (item, type) => {
    console.log('Sending email...', {
      type,
      itemId: item._id,
      clientId: item.clientId,
      subject: `${type.charAt(0).toUpperCase() + type.slice(1)} ${item.number}`
    });

    const client = clients.find(c => c._id === item.clientId);
    if (client) {
      alert(`Email would be sent to ${client.email} with ${type} ${item.number}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-gray-600">Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-6 m-4 rounded-lg">
        <h4 className="text-lg font-semibold mb-2">Error Loading Data</h4>
        <p className="mb-4">{error}</p>
        <button 
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  const commonProps = {
    clients,
    invoices,
    quotes,
    openModal,
    deleteItem: handleDeleteItem,
    convertQuoteToInvoice: handleConvertQuoteToInvoice,
    settings,
    onSendEmail: sendEmail,
    onRecordPayment: (invoice) => openModal('payment', invoice),
    onViewPayments: (invoice) => openModal('paymentHistory', invoice),
    onProcessPayment: handleProcessPayment,
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/admin/' || path === '/admin') return 'Dashboard';
    if (path.startsWith('/admin/clients')) return 'Clients';
    if (path.startsWith('/admin/invoices')) return 'Invoices';
    if (path.startsWith('/admin/quotes')) return 'Quotes';
    if (path.startsWith('/admin/reports')) return 'Reports';
    if (path.startsWith('/admin/settings')) return 'Settings';
    return 'Admin Portal';
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-30">
        <h1 className="text-lg font-semibold text-gray-800">{getPageTitle()}</h1>
        <button 
          className="p-2 rounded-md hover:bg-gray-100 transition-colors"
          onClick={toggleMobileSidebar}
          title="Open menu"
        >
          <Menu size={24} className="text-gray-600" />
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={toggleMobileSidebar}
        />
      )}

      {/* Sidebar Container */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out
        ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <AdminSidebar
          user={user}
          settings={settings}
          onLogout={handleLogout}
          isMobileOpen={mobileSidebarOpen}
          onToggleMobile={toggleMobileSidebar}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        {/* Content Area */}
        <main className="flex-1 p-4 lg:p-6 pt-20 lg:pt-6 overflow-auto">
          <Routes>
            <Route path="/" element={
              <AdminDashboardHome 
                clients={clients}
                invoices={invoices}
                quotes={quotes}
                settings={settings}
                onNavigate={handleNavigate}
              />
            } />
            <Route path="/clients" element={
              <ClientsPage {...commonProps} />
            } />
            <Route path="/invoices" element={
              <InvoicesPage {...commonProps} />
            } />
            <Route path="/quotes" element={
              <QuotesPage {...commonProps} />
            } />
            <Route path="/reports" element={
              <ReportsPage {...commonProps} />
            } />
            <Route path="/settings/*" element={
              <div>
                <h3 className="text-2xl font-semibold mb-6 text-gray-800">Settings</h3>
                <AdminSettings 
                  settings={settings} 
                  onSave={saveSettings}
                  userPermissions={userPermissions}
                />
              </div>
            } />
            <Route path="*" element={<Navigate to="/admin/" replace />} />
          </Routes>
        </main>
      </div>

      {/* Modals */}
      {showModal && modalType === 'client' && (
        <ClientModal
          client={editingItem}
          invoices={invoices.filter(inv => inv.clientId === editingItem?._id)}
          quotes={quotes.filter(q => q.clientId === editingItem?._id)}
          documents={currentClientDocuments}
          settings={settings}
          onSave={handleSaveClient}
          onClose={closeModal}
          onUploadDocument={handleUploadDocument}
          onDeleteDocument={handleDeleteDocument}
          onUpdateDocument={handleUpdateDocument}
          onDownloadDocument={downloadDocument}
        />
      )}

      {showModal && modalType === 'invoice' && (
        <InvoiceModal
          invoice={editingItem}
          clients={clients}
          settings={settings}
          onSave={handleSaveInvoice}
          onSendEmail={sendEmail}
          onClose={closeModal}
        />
      )}

      {showModal && modalType === 'quote' && (
        <QuoteModal
          quote={editingItem}
          clients={clients}
          settings={settings}
          onSave={handleSaveQuote}
          onClose={closeModal}
        />
      )}



      {showModal && modalType === 'payment' && (
        <PaymentModal
          invoice={editingItem}
          settings={settings}
          onSave={handleRecordPayment}
          onClose={closeModal}
        />
      )}

      {showModal && modalType === 'paymentHistory' && (
        <PaymentHistoryModal
          invoice={editingItem}
          clients={clients}
          settings={settings}
          onClose={closeModal}
          onDeletePayment={handleDeletePayment}
          onEditPayment={(payment) => {
            setEditingPayment(payment);
            setModalType('editPayment');
          }}
        />
      )}

      {showModal && modalType === 'editPayment' && (
        <PaymentModal
          invoice={editingItem}
          payment={editingPayment}
          settings={settings}
          onSave={handleRecordPayment}
          onClose={closeModal}
        />
      )}

      {showModal && modalType === 'processPayment' && (
        <AdminPaymentProcessingModal
          invoice={editingItem}
          client={editingItem?.client}
          onClose={closeModal}
          onPaymentProcessed={handlePaymentProcessed}
        />
      )}
    </div>
  );
};

export default AdminApp;